import { useState, useRef } from "react";
import "./search-view.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTour } from "./hooks/useTour";
import { FaSearch, FaExchangeAlt, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { getSearchApiUrl} from "./utils/apiUrlList";
import { TRACK_EVENT_SELECTION_KEY } from "./TrackEventSelector";
import {
  TRACK_LIST_API_test,
  API_CATEGORY_LIST_URL,
  API_ROUNDS_LIST_URL,
  API_SAVED_SEARCH_URL,
} from "./utils/apiUrlList";

import toast from "react-hot-toast";

import { useAuth } from "./AuthProvider";

import {api} from "./utils/apiClientWithCsrf"

import { useDomainConfig } from "./utils/trackListFilter";

const SEARCH_TOUR_STEPS = () => [
  {
    element: '.search-container-content',
    popover: {
      title: 'Search Filters',
      description: 'Filter runs by date range, category, round, class, driver name, or car number. Tap "Search criteria" to expand the form.',
    },
  },
  {
    popover: {
      title: 'Result Cards',
      description: 'Each result shows the driver, car, class, and run date. Tap "Time Slip" to view the full timing breakdown.',
    },
  },
  {
    popover: {
      title: 'Compare Runs',
      description: 'Check the "Compare" box on two results, then tap ⇄ to see them side by side.',
    },
  },
];

const SearchView = () => {
  const location = useLocation();

  // Debug component lifecycle
  useEffect(() => {
    console.log("[DEBUG_LOG] SearchView component mounted");
    return () => {
      console.log("[DEBUG_LOG] SearchView component unmounted");
    };
  }, []);


  const { user } = useAuth();
  const userId = user?.id ?? null;

  const [events, setEvents] = useState([]);
  const [carNumber, setCarNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [searchName, setSearchName] = useState("");
  const [criteriaId, setCriteriaId] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [carClass, setCarClass] = useState("");
  const [EtRangeFrom, setEtRangeFrom] = useState("");
  const [EtRangeTo, setEtRangeTo] = useState("");
  const [results, setResults] = useState(events);

  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [showSortHint, setShowSortHint] = useState(false);

  const [data, setData] = useState([]);

  const [increRaceIdList, setIncreRaceIdList] = useState([]);
  const [listFlag, setListFlag] = useState(0);

  // State for card comparison feature
  const [selectedCards, setSelectedCards] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  // State to track if localStorage data has been loaded
  const [dataLoaded, setDataLoaded] = useState(false);

  const { startIfNew } = useTour('search', SEARCH_TOUR_STEPS);

  useEffect(() => {
    if (dataLoaded) startIfNew();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded]);

  // Collapsible criteria panel — start collapsed when arriving via an
  // auto-search link (carNumber/driverName/carClass click on the Time Slip view).
  const [criteriaCollapsed, setCriteriaCollapsed] = useState(
    () => Boolean(location.state?.autoSearch?.field && location.state?.autoSearch?.value)
  );

  // Infinite scroll state — sentinel intersects → load next page until empty.
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);

  // Reference to the criteria container so the auto-collapse effect can
  // measure when the form has fully scrolled past the navbar.
  const criteriaRef = useRef(null);


  const [searchPage, setSearchPage] = useState(0);

  const [trackNamevalue, setTrackNamevalue] = useState("");
  const [selectedEventValue, setSelectedEventValue] = useState("");
  const [selectedCategoryValue, setSelectedCategoryValue] = useState("");

  const [selectedRoundValue, setSelectedRoundValue] = useState("");
  const [trackName, setTrackName] = useState("-");
  const [trackIdValue, setTrackIdValue] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  const [eventName, setEventName] = useState("-");
  const [categoryList, setCategoryList] = useState([]);
  const [roundList, setroundList] = useState([]);
  const [savedSearchList, setSavedSearchList] = useState([]);
  const [selectedFilterName, setSelectedFilterName] = useState("0");
  const [eventIdValue, setEventIdValue] = useState("");
  const [categoryValue, setCategoryValue] = useState("");


   const domainConfig = useDomainConfig();
  
      useEffect(() => {
    if (!domainConfig) return;  // Wait for domain to load
    
    const savedSelection = localStorage.getItem(TRACK_EVENT_SELECTION_KEY);
    if (!savedSelection) return;
    
    const { trackName } = JSON.parse(savedSelection); 

    if (domainConfig !== "all tracks" && trackName !== domainConfig) {
      alert("Access to this race is restricted.");
      navigate("/editTrackEvent");
    }
  }, [domainConfig]);

  // Debug dataLoaded state changes
  useEffect(() => {
    console.log("[DEBUG_LOG] dataLoaded state changed:", dataLoaded);
  }, [dataLoaded]);

  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split("-");
    return `${year}-${month}-${day} 00:00:00.000000 `;
  };

  const formatToDate = (dateString) => {
    const [year, month, day] = dateString.split("-");
    return `${year}-${month}-${day} 23:59:00.000000 `;
  };

  const handleSearchData = async () => {
    console.log("[DEBUG_LOG] Search form submitted with criteria:", {
      fromDate: fromDate || "empty",
      toDate: toDate || "empty",
      category: selectedCategoryValue || "empty",
      round: selectedRoundValue || "empty",
      event: eventName || "empty",
      track: trackNamevalue || "empty",
      trackId: trackIdValue || "empty",
      carClass: carClass || "empty",
      driverName: driverName || "empty",
      carNumber: carNumber || "empty",
      EtRangeFrom: EtRangeFrom || "empty",
      EtRangeTo: EtRangeTo || "empty",
      sortBy: sortBy || "empty",
      sortOrder: sortOrder || "empty",
    });

    const requestData = {
      eventFromDate: fromDate ? formatDate(fromDate) : "",
      eventToDate: toDate ? formatToDate(toDate) : "",
      category: selectedCategoryValue,
      round: selectedRoundValue,
      trackId: trackIdValue,
      carClass: carClass,
      driverName: driverName,
      carNo: carNumber,
      startET: EtRangeFrom,
      endET: EtRangeTo,
      eventId: eventIdValue,
      sortBy: sortBy,
      sortOrder: sortOrder,
    };

    const apiUrl = getSearchApiUrl(searchPage);

    try {
      console.log("[DEBUG_LOG] Sending search request to API");
      const response = await api.post(apiUrl, requestData, {
        withCredentials: true,
      });
      const data = response.data;
      console.log(
        "[DEBUG_LOG] Received search results from API, count:",
        data.length,
      );
      const increRaceIdList = data.map((event) => event.raceKey);
      console.log(
        "[DEBUG_LOG] Extracted increRaceIdList, length:",
        increRaceIdList.length,
      );

      setIncreRaceIdList((prevList) => [...prevList, ...increRaceIdList]);
      setListFlag(1);
      console.log("[DEBUG_LOG] Set listFlag to 1");

      const eventResults = data.map((event) => ({
        carNumber: event.carNo,
        carClass: event.carClass,
        driver: event.driver,
        date: event.dateTimeStamp,
        round: event.round,
        win: event.win || "",
        race_day_race_id: event.raceDayRaceId,
        dateOnly: event.dateTimeStamp,
        raceKey: event.raceKey,
        raceUniqueId: event.raceUniqueId,
        dial_in:
          typeof event.dialIn === "number"
            ? event.dialIn.toFixed(3)
            : event.dialIn || "NA",
        rt:
          typeof event.rt === "number" ? event.rt.toFixed(3) : event.rt || "NA",
        ft60:
          typeof event.ft60 === "number"
            ? event.ft60.toFixed(3)
            : event.ft60 || "NA",
        ft330:
          typeof event.ft330 === "number"
            ? event.ft330.toFixed(3)
            : event.ft330 || "NA",
        ft8:
          typeof event.ft8 === "number"
            ? event.ft8.toFixed(3)
            : event.ft8 || "NA",
        ft8mph:
          typeof event.ft8mph === "number"
            ? event.ft8mph.toFixed(3)
            : event.ft8mph || "NA",
        ft1000:
          typeof event.ft1000 === "number"
            ? event.ft1000.toFixed(3)
            : event.ft1000 || "NA",
        ft4:
          typeof event.ft4 === "number"
            ? event.ft4.toFixed(3)
            : event.ft4 || "NA",
        ft4mph:
          typeof event.ft4mph === "number"
            ? event.ft4mph.toFixed(3)
            : event.ft4mph || "NA",
        opponentDriverName: event.opponentDriver
          ? event.opponentDriver.driver
          : "NA",
        opponentDriverCarNo: event.opponentDriver
          ? event.opponentDriver.carNo
          : "NA",
      }));
      console.log(
        "[DEBUG_LOG] Mapped API results to eventResults, count:",
        eventResults.length,
      );
      setResults((prevResults) => [...prevResults, ...eventResults]);
      console.log("[DEBUG_LOG] Updated results state with eventResults");
      if (data.length === 0) setHasMore(false);
      return data;
    } catch (error) {
      console.error("[DEBUG_LOG] Error fetching data:", error);
      throw error;
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearchDataClearButton = async () => {
    console.log("[DEBUG_LOG] Search form submitted with criteria:", {
      fromDate: fromDate || "empty",
      toDate: toDate || "empty",
      category: selectedCategoryValue || "empty",
      round: selectedRoundValue || "empty",
      event: selectedEventValue || "empty",
      track: trackNamevalue || "empty",
      trackId: trackIdValue || "empty",
      carClass: carClass || "empty",
      driverName: driverName || "empty",
      carNumber: carNumber || "empty",
      EtRangeFrom: EtRangeFrom || "empty",
      EtRangeTo: EtRangeTo || "empty",
      sortBy: sortBy || "empty",
      sortOrder: sortOrder || "empty",
    });

    const requestData = {
      eventFromDate: "",
      eventToDate: "",
      category: "",
      round: "",
      trackId: trackIdValue,
      carClass: "",
      driverName: "",
      carNo: "",
      startET: "",
      endET: "",
      eventId: eventIdValue,
      sortBy: "",
      sortOrder: "",
    };

    const apiUrl = getSearchApiUrl(searchPage);

    try {
      console.log("[DEBUG_LOG] Sending search request to API");
      const response = await api.post(apiUrl, requestData, {
        withCredentials: true,
      });
      const data = response.data;
      console.log(
        "[DEBUG_LOG] Received search results from API, count:",
        data.length,
      );
      const increRaceIdList = data.map((event) => event.raceKey);
      console.log(
        "[DEBUG_LOG] Extracted increRaceIdList, length:",
        increRaceIdList.length,
      );

      setIncreRaceIdList((prevList) => [...prevList, ...increRaceIdList]);
      setListFlag(1);
      console.log("[DEBUG_LOG] Set listFlag to 1");

      const eventResults = data.map((event) => ({
        carNumber: event.carNo,
        carClass: event.carClass,
        driver: event.driver,
        date: event.dateTimeStamp,
        round: event.round,
        win: event.win || "",
        race_day_race_id: event.raceDayRaceId,
        dateOnly: event.dateTimeStamp,
        raceKey: event.raceKey,
        raceUniqueId: event.raceUniqueId,
        dial_in:
          typeof event.dialIn === "number"
            ? event.dialIn.toFixed(3)
            : event.dialIn || "NA",
        rt:
          typeof event.rt === "number" ? event.rt.toFixed(3) : event.rt || "NA",
        ft60:
          typeof event.ft60 === "number"
            ? event.ft60.toFixed(3)
            : event.ft60 || "NA",
        ft330:
          typeof event.ft330 === "number"
            ? event.ft330.toFixed(3)
            : event.ft330 || "NA",
        ft8:
          typeof event.ft8 === "number"
            ? event.ft8.toFixed(3)
            : event.ft8 || "NA",
        ft8mph:
          typeof event.ft8mph === "number"
            ? event.ft8mph.toFixed(3)
            : event.ft8mph || "NA",
        ft1000:
          typeof event.ft1000 === "number"
            ? event.ft1000.toFixed(3)
            : event.ft1000 || "NA",
        ft4:
          typeof event.ft4 === "number"
            ? event.ft4.toFixed(3)
            : event.ft4 || "NA",
        ft4mph:
          typeof event.ft4mph === "number"
            ? event.ft4mph.toFixed(3)
            : event.ft4mph || "NA",
        opponentDriverName: event.opponentDriver
          ? event.opponentDriver.driver
          : "NA",
        opponentDriverCarNo: event.opponentDriver
          ? event.opponentDriver.carNo
          : "NA",
      }));
      console.log(
        "[DEBUG_LOG] Mapped API results to eventResults, count:",
        eventResults.length,
      );
      setResults(eventResults);
      console.log("[DEBUG_LOG] Updated results state with eventResults");
      if (data.length === 0) setHasMore(false);
      return data;
    } catch (error) {
      console.error("[DEBUG_LOG] Error fetching data:", error);
      throw error;
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (sortBy && !sortOrder) {
      setShowSortHint(true);
      return;
    }
    setSearchPage(0);
    setResults([]);
    setIncreRaceIdList([]);
    setHasMore(true);
    setCriteriaCollapsed(true);
    await handleSearchData();
  };

  useEffect(() => {
    if (listFlag === 1) {
      handleSearchData();
    }
  }, [searchPage]);

  // Auto-collapse the criteria panel once the user has scrolled fully past
  // the form (its bottom edge is now above the navbar). On small screens the
  // form can be taller than the viewport, so we only collapse when the user
  // is clearly done with it — not on partial scrolls inside the form.
  useEffect(() => {
    if (criteriaCollapsed) return undefined;
    const onScroll = () => {
      const el = criteriaRef.current;
      if (!el) return;
      const navH =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            "--navbar-height",
          ),
        ) || 90;
      if (el.getBoundingClientRect().bottom < navH) {
        setCriteriaCollapsed(true);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [criteriaCollapsed]);

  // Infinite scroll — observe the sentinel; when intersecting, advance page.
  // Re-attach whenever the sentinel might have been re-mounted (results going
  // from empty back to non-empty during a fresh search), otherwise the
  // observer stays bound to a detached DOM node and never fires again.
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore || loadingMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setLoadingMore(true);
        if (listFlag === 1) {
          setSearchPage((p) => p + 1);
        } else {
          setPage((p) => (typeof p === "number" ? p + 1 : 1));
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, listFlag, results.length > 0]);

  const handleOnLoadMoreSearch = () => {
    console.log(
      "[DEBUG_LOG] Load More button clicked for seach, current page:",
      searchPage,
    );
    setSearchPage((prev) => prev + 1);
    console.log("[DEBUG_LOG] Page incremented to:", searchPage + 1);
  };

  const navigate = useNavigate();
  const handleClick = (
    date,
    race_day_race_id,
    increRaceId,
    raceKeyid,
    increRaceIdList,
    listFlag,
  ) => {
    console.log(
      "[DEBUG_LOG] Navigating to TimeSlipView from SearchView with:",
      {
        date,
        race_day_race_id,
        increRaceId,
        raceKeyid,
        increRaceIdListLength: increRaceIdList.length,
        listFlag,
      },
    );
    // Build the current search payload so TimeSlipView can fetch additional
    // pages on swipe without re-deriving the criteria.
    const searchContext = listFlag === 1
      ? {
          payload: {
            eventFromDate: fromDate ? formatDate(fromDate) : "",
            eventToDate: toDate ? formatToDate(toDate) : "",
            category: selectedCategoryValue,
            round: selectedRoundValue,
            trackId: trackIdValue,
            carClass: carClass,
            driverName: driverName,
            carNo: carNumber,
            startET: EtRangeFrom,
            endET: EtRangeTo,
            eventId: eventIdValue,
            sortBy: sortBy,
            sortOrder: sortOrder,
          },
          searchPage: searchPage,
        }
      : null;

    navigate("/timeslip", {
      state: {
        date: date,
        race_day_race_id: race_day_race_id,
        increRaceId: increRaceId,
        increRaceIdList: increRaceIdList,
        raceKeyid: raceKeyid,
        listFlag: listFlag,
        searchContext: searchContext,
      },
    });
    console.log("[DEBUG_LOG] Navigation to TimeSlipView complete");
  };

  const formatDateRaceUniqueId = (timestamp) => {
    try {
      const datePart = timestamp.substring(0, 10);
      const [month, day, year] = datePart.split("-");

      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthName = monthNames[parseInt(month, 10) - 1];

      const timePart =
        timestamp.substring(11, 13) +
        ":" +
        timestamp.substring(14, 16) +
        ":" +
        timestamp.substring(17, 19);

      return `${day}/${monthName}/${year} ${timePart}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return { formattedDate: "NA", formattedTime: "NA" };
    }
  };

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState();

  useEffect(() => {
    if (page === undefined) {
      setPage(0);
    }
    if (page !== undefined) {
      const fetchItems = async () => {
        console.log("[DEBUG_LOG] fetchItems called for page:", page);
        console.log("[DEBUG_LOG] Set listFlag to 0");
        try {
          console.log("[DEBUG_LOG] Fetching data from API for page:", page);

          const requestData = {
            eventFromDate: fromDate ? formatDate(fromDate) : "",
            eventToDate: toDate ? formatToDate(toDate) : "",
            category: selectedCategoryValue,
            round: selectedRoundValue,
            trackId: trackIdValue,
            carClass: carClass,
            driverName: driverName,
            carNo: carNumber,
            startET: EtRangeFrom,
            endET: EtRangeTo,
            eventId: eventIdValue,
            sortBy: sortBy,
            sortOrder: sortOrder,
          };

          const apiUrl = getSearchApiUrl(page);

          const response = await api.post(apiUrl, requestData, {
            withCredentials: true,
          });
          if (response.data.length > 0) {
            const data = response.data;
            console.log(
              "[DEBUG_LOG] Received data from API, count:",
              data.length,
            );

            const increRaceIdList = data.map((event) => event.raceKey);
      console.log(
        "[DEBUG_LOG] Extracted increRaceIdList, length:",
        increRaceIdList.length,
      );

      setIncreRaceIdList((prevList) => [...prevList, ...increRaceIdList]);
       setListFlag(1);

            const eventResults = data.map((event) => ({
              carNumber: event.carNo,
              carClass: event.carClass,
              driver: event.driver,
              date: event.dateTimeStamp,
              round: event.round,
              win: event.win || "",
              race_day_race_id: event.raceDayRaceId,
              dateOnly: event.dateTimeStamp,
              raceKey: event.raceKey,
              raceUniqueId: event.raceUniqueId,
              dial_in:
                typeof event.dialIn === "number"
                  ? event.dialIn.toFixed(3)
                  : event.dialIn || "NA",
              rt:
                typeof event.rt === "number"
                  ? event.rt.toFixed(3)
                  : event.rt || "NA",
              ft60:
                typeof event.ft60 === "number"
                  ? event.ft60.toFixed(3)
                  : event.ft60 || "NA",
              ft330:
                typeof event.ft330 === "number"
                  ? event.ft330.toFixed(3)
                  : event.ft330 || "NA",
              ft8:
                typeof event.ft8 === "number"
                  ? event.ft8.toFixed(3)
                  : event.ft8 || "NA",
              ft8mph:
                typeof event.ft8mph === "number"
                  ? event.ft8mph.toFixed(3)
                  : event.ft8mph || "NA",
              ft1000:
                typeof event.ft1000 === "number"
                  ? event.ft1000.toFixed(3)
                  : event.ft1000 || "NA",
              ft4:
                typeof event.ft4 === "number"
                  ? event.ft4.toFixed(3)
                  : event.ft4 || "NA",
              ft4mph:
                typeof event.ft4mph === "number"
                  ? event.ft4mph.toFixed(3)
                  : event.ft4mph || "NA",
              opponentDriverName: event.opponentDriver
                ? event.opponentDriver.driver
                : "NA",
              opponentDriverCarNo: event.opponentDriver
                ? event.opponentDriver.carNo
                : "NA",
            }));
            console.log(
              "[DEBUG_LOG] Mapped API data to eventResults, count:",
              eventResults.length,
            );

            setEvents((prev) => {
              const newEvents = [...prev, ...eventResults];
              console.log(
                "[DEBUG_LOG] Updated events state, new length:",
                newEvents.length,
              );
              return newEvents;
            });
            if (page === 0) {
              setResults(eventResults); 
            } else {
              setResults((prev) => [...prev, ...eventResults]); 
            }
            setData((prev) => {
              const newData = [...prev, ...eventResults];
              console.log(
                "[DEBUG_LOG] Updated data state, new length:",
                newData.length,
              );
              return newData;
            });
          } else {
            console.log(
              "[DEBUG_LOG] No data received from API for page:",
              page,
            );
            setHasMore(false);
          }
        } catch (error) {
          console.error("[DEBUG_LOG] Error fetching data:", error);
        } finally {
          setLoading(false);
          setLoadingMore(false);
          console.log("[DEBUG_LOG] Loading state set to false");
        }
      };
      fetchItems();
    }
  }, [page]);

  const handleOnLoadMore = () => {
    console.log("[DEBUG_LOG] Load More button clicked, current page:", page);
    setPage((prev) => prev + 1);
    console.log("[DEBUG_LOG] Page incremented to:", page + 1);
  };

  const handleClear = async (e) => {
    if (e) e.preventDefault();
    console.log("[DEBUG_LOG] Clearing search criteria and results");

    setFromDate("");
    setToDate("");
    setSelectedCategoryValue("");
    setSelectedRoundValue("");
    setCarClass("");
    setDriverName("");
    setCarNumber("");
    setEtRangeFrom("");
    setEtRangeTo("");
    setSortBy("");
    setSortOrder("");
    setSearchName("");
    setCriteriaId("");
    setSavedSearchList([])
    setSelectedFilterName("0");

    localStorage.removeItem("searchCriteria");
    localStorage.removeItem("searchResults");
    localStorage.removeItem("listFlag");
    localStorage.removeItem("increRaceIdList");
    localStorage.removeItem("searchPage");
    console.log(
      "[DEBUG_LOG] Cleared localStorage items: searchCriteria, searchResults, listFlag, increRaceIdList, searchPage",
    );
    setListFlag(0);
    setSearchPage(0);
    setPage(0);
    setHasMore(true);
    await handleSearchDataClearButton();
    console.log("[DEBUG_LOG] Reset results and listFlag");
  };

  const handleSaveSearch = async () => {
    const csrfToken = document.cookie
  .split('; ')
  .find(row => row.startsWith('XSRF-TOKEN='))
  ?.split('=')[1];

    const SaveSearchRequest = {
      name: searchName,
      userId: userId,
      eventId: eventIdValue,
      id: criteriaId,
      criteria: {
      eventFromDate: fromDate ? formatDate(fromDate) : "",
      eventToDate: toDate ? formatToDate(toDate) : "",
      category: selectedCategoryValue,
      round: selectedRoundValue,
      trackId: trackIdValue,
      carClass: carClass,
      driverName: driverName,
      carNo: carNumber,
      startET: EtRangeFrom,
      endET: EtRangeTo,
      eventId: eventIdValue,
      sortBy: sortBy,
      sortOrder: sortOrder,
      },
  }
  try{
    console.log("the token is csrfToken", csrfToken)
    const response = await api.post(API_SAVED_SEARCH_URL, SaveSearchRequest, {
    headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': csrfToken
      },
      withCredentials: true
    });
    toast.success("Search saved successfully!");
    setCriteriaId(response.data.id);
    setSavedSearchList((prevList) => [...prevList, response.data]);
    setSelectedFilterName(response.data.name);
  } catch (error) {
    console.error("[DEBUG_LOG] Error saving search:", error);
    toast.error("Failed to save search.");
  }
}

  const handleDeleteReportType = async (criteriaId, e) => {
    const csrfToken = document.cookie
  .split('; ')
  .find(row => row.startsWith('XSRF-TOKEN='))
  ?.split('=')[1];

    try {
      await api.delete(`${API_SAVED_SEARCH_URL}/${criteriaId}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': csrfToken
        },
        withCredentials: true
      });
      toast.success("Search deleted successfully!");
      handleClear(e);
    } catch (error) {
      console.error("[DEBUG_LOG] Error deleting report type:", error);
      toast.error("Failed to delete search.");
    }
  };

  // Create a unique identifier for each card. raceKey is the race's primary
  // key, so it's unique per result row even when the same driver/car appears
  // across multiple races.
  const getCardUniqueId = (card) => {
    return `${card.raceKey}-${card.driver}-${card.carNumber}`;
  };

  // Handle card selection for comparison
  const handleCardSelection = (result) => {
    console.log(
      "[DEBUG_LOG] Card selection triggered for:",
      getCardUniqueId(result),
    );

    setSelectedCards((prevSelected) => {
      const resultUniqueId = getCardUniqueId(result);

      // If card is already selected, remove it
      if (
        prevSelected.some((card) => getCardUniqueId(card) === resultUniqueId)
      ) {
        console.log(
          "[DEBUG_LOG] Removing card from selection:",
          resultUniqueId,
        );
        return prevSelected.filter(
          (card) => getCardUniqueId(card) !== resultUniqueId,
        );
      }

      // If already have 3 cards selected, replace the oldest one
      if (prevSelected.length >= 3) {
        console.log(
          "[DEBUG_LOG] Already have 3 cards, replacing oldest with:",
          resultUniqueId,
        );
        return [...prevSelected.slice(1), result];
      }

      // Otherwise add the card to selection
      console.log("[DEBUG_LOG] Adding card to selection:", resultUniqueId);
      return [...prevSelected, result];
    });
  };

  // Load selected cards, search criteria, and search results from localStorage when component mounts or when navigating back to this component
  useEffect(() => {
    // Set dataLoaded to false at the start of loading
    setDataLoaded(false);
    console.log(
      "[DEBUG_LOG] Loading data from localStorage, location.key:",
      location.key,
    );

    const autoSearch = location.state?.autoSearch;
    if (autoSearch?.field && autoSearch?.value) {
      console.log(
        "[DEBUG_LOG] Auto-search requested:",
        autoSearch.field,
        autoSearch.value,
      );

      // Wipe persisted search state — "as if Clear was clicked"
      localStorage.removeItem("searchCriteria");
      localStorage.removeItem("searchResults");
      localStorage.removeItem("listFlag");
      localStorage.removeItem("increRaceIdList");
      localStorage.removeItem("selectedCards");
      localStorage.removeItem("searchPage");

      // Reset all criteria
      setFromDate("");
      setToDate("");
      setSelectedCategoryValue("");
      setSelectedRoundValue("");
      setCarClass("");
      setDriverName("");
      setCarNumber("");
      setEtRangeFrom("");
      setEtRangeTo("");
      setCategoryValue("");

      // Set only the targeted field
      if (autoSearch.field === "carNumber") setCarNumber(autoSearch.value);
      else if (autoSearch.field === "driverName") setDriverName(autoSearch.value);
      else if (autoSearch.field === "carClass") setCarClass(autoSearch.value);

      // Reset result-related state
      setResults([]);
      setIncreRaceIdList([]);
      setListFlag(0);
      setSelectedCards([]);
      setHasMore(true);
      setSearchPage(0);

      // Restore track/event context so the search is scoped correctly
      try {
        const savedSelection = localStorage.getItem(TRACK_EVENT_SELECTION_KEY);
        if (savedSelection) {
          const {
            trackName: savedTrack,
            eventId: savedEventId,
            eventName: savedEventName,
            trackId: savedTrackId,
            startDate: savedStartDate,
            endDate: savedEndDate,
          } = JSON.parse(savedSelection);
          if (savedTrack) setTrackNamevalue(savedTrack);
          if (savedEventName) {
            setSelectedEventValue(savedEventName);
            setEventName(savedEventName);
          }
          if (savedEventId) setEventIdValue(String(savedEventId));
          if (savedTrackId) setTrackIdValue(String(savedTrackId));
          if (savedStartDate) setStartDate(savedStartDate);
          if (savedEndDate) setEndDate(savedEndDate);
        }
      } catch (error) {
        console.error(
          "[DEBUG_LOG] Error restoring track context for car search:",
          error,
        );
      }

      // The existing [page] effect runs fetchItems on the page=undefined→0
      // transition. Because the criteria setState calls above land before the
      // [page] effect re-fires, its closure picks up the new carNumber and
      // cleared filters — no extra trigger needed.

      setDataLoaded(true);
      return;
    }

    try {
      // Load selected cards
      const savedCards = localStorage.getItem("selectedCards");
      if (savedCards) {
        console.log(
          "[DEBUG_LOG] Found saved cards:",
          JSON.parse(savedCards).length,
        );
        setSelectedCards(JSON.parse(savedCards));
      } else {
        console.log("[DEBUG_LOG] No saved cards found");
      }

      // Load search criteria
      const savedCriteria = localStorage.getItem("searchCriteria");
      const trackEventSelectionValue = localStorage.getItem(
        "trackEventSelection",
      );
      if (trackEventSelectionValue) {
        setSelectedEventValue(trackEventSelectionValue.eventName || "");
        setTrackNamevalue(trackEventSelectionValue.trackName || "");
      }
      if (savedCriteria) {
        console.log("[DEBUG_LOG] Found saved criteria");
        const criteria = JSON.parse(savedCriteria);
        setFromDate(criteria.fromDate || "");
        setToDate(criteria.toDate || "");
        setSelectedCategoryValue(criteria.category || "");
        setSelectedRoundValue(criteria.round || "");
        setCarClass(criteria.carClass || "");
        setDriverName(criteria.driverName || "");
        setCarNumber(criteria.carNumber || "");
        setEtRangeFrom(criteria.etRangeFrom || "");
        setEtRangeTo(criteria.etRangeTo || "");
        setSortBy(criteria.sortBy || "");
        setSortOrder(criteria.sortOrder || "");
        setSelectedCategoryValue(criteria.category || "");
        setSelectedRoundValue(criteria.round || "");
        setEventIdValue(criteria.eventId || "");
        setCategoryValue(criteria.categoryValue || "");
      } else {
        console.log("[DEBUG_LOG] No saved criteria found");
      }

      // Load search results
      const savedResults = localStorage.getItem("searchResults");
      if (savedResults && results.length === 0) {
        console.log(
          "[DEBUG_LOG] Found saved results:",
          JSON.parse(savedResults).length,
        );
        setResults(JSON.parse(savedResults));
      } else {
        console.log("[DEBUG_LOG] No saved results found");
      }

      // Load search state
      const savedListFlag = localStorage.getItem("listFlag");
      if (savedListFlag) {
        console.log(
          "[DEBUG_LOG] Found saved listFlag:",
          JSON.parse(savedListFlag),
        );
        setListFlag(JSON.parse(savedListFlag));
      } else {
        console.log("[DEBUG_LOG] No saved listFlag found");
      }

      const savedSearchPage = localStorage.getItem("searchPage");
      if (savedSearchPage) {
        const n = Number(JSON.parse(savedSearchPage));
        if (Number.isFinite(n)) setSearchPage(n);
      }

      const savedIncreRaceIdList = localStorage.getItem("increRaceIdList");
      if (savedIncreRaceIdList) {
        console.log(
          "[DEBUG_LOG] Found saved increRaceIdList:",
          JSON.parse(savedIncreRaceIdList).length,
        );
        setIncreRaceIdList(JSON.parse(savedIncreRaceIdList));
      } else {
        console.log("[DEBUG_LOG] No saved increRaceIdList found");
      }

      // Restore track/event selection — only if not already set by search criteria
      // This ensures the track & event dropdowns are pre-filled from the selector page
      const savedSelection = localStorage.getItem(TRACK_EVENT_SELECTION_KEY);
      if (savedSelection) {
        const {
          trackName: savedTrack,
          eventId: savedEventId,
          eventName: savedEventName,
          trackId: savedTrackId,
          startDate: savedStartDate,
          endDate: savedEndDate,
        } = JSON.parse(savedSelection);
        // Only apply if the search criteria didn't already restore a track/event
        const savedCriteria = localStorage.getItem("searchCriteria");
        const hasCriteria = savedCriteria
          ? JSON.parse(savedCriteria).track
          : false;
        if (!hasCriteria) {
          if (savedTrack) setTrackNamevalue(savedTrack);
          if (savedEventName) setSelectedEventValue(savedEventName);
          if (savedEventName) setEventName(savedEventName);
          if (savedEventId) setEventIdValue(String(savedEventId));
          if (savedTrackId) setTrackIdValue(String(savedTrackId));
          if (savedStartDate) setStartDate(savedStartDate);
          if (savedEndDate) setEndDate(savedEndDate);
        }
      }
    } catch (error) {
      console.error("[DEBUG_LOG] Error loading data from localStorage:", error);
    } finally {
      // Set dataLoaded to true after all data has been loaded or if there was an error
      console.log(
        "[DEBUG_LOG] Data loading complete, setting dataLoaded to true",
      );
      setDataLoaded(true);
    }
  }, [location.key]); // Use location.key instead of pathname to ensure this runs on every navigation

  // Save selected cards to localStorage when they change
  useEffect(() => {
    console.log(
      "[DEBUG_LOG] selectedCards changed, saving to localStorage:",
      selectedCards.length,
    );
    localStorage.setItem("selectedCards", JSON.stringify(selectedCards));
  }, [selectedCards]);

  // Save search criteria to localStorage when they change
  useEffect(() => {
    if (!dataLoaded) return;
    const searchCriteria = {
      fromDate: fromDate || "",
      toDate: toDate || "",
      selectedCategoryValue,
      selectedRoundValue,
      carClass,
      driverName,
      carNumber,
      etRangeFrom: EtRangeFrom,
      etRangeTo: EtRangeTo,
      category: selectedCategoryValue,
      round: selectedRoundValue,
      categoryValue: categoryValue,
      sortBy: sortBy,
      sortOrder: sortOrder,
    };
    console.log(
      "[DEBUG_LOG] Search criteria changed, saving to localStorage:",
      JSON.stringify({
        fromDate: fromDate || "empty",
        toDate: toDate || "empty",
        category: selectedCategoryValue || "empty",
        round: selectedRoundValue || "empty",
        event: selectedEventValue || "empty",
        track: trackNamevalue || "empty",
        trackId: trackIdValue || "empty",
        carClass: carClass || "empty",
        driverName: driverName || "empty",
        carNumber: carNumber || "empty",
        etRangeFrom: EtRangeFrom || "empty",
        etRangeTo: EtRangeTo || "empty",
        sortBy: sortBy || "empty",
        sortOrder: sortOrder || "empty",
      }),
    );
    localStorage.setItem("searchCriteria", JSON.stringify(searchCriteria));
  }, [
    fromDate,
    toDate,
    selectedCategoryValue,
    selectedRoundValue,
    carClass,
    driverName,
    carNumber,
    EtRangeFrom,
    EtRangeTo,
    dataLoaded,
    sortBy,
    sortOrder,
  ]);

  // Save search results to localStorage when they change
  useEffect(() => {
    console.log(
      "[DEBUG_LOG] results changed, saving to localStorage:",
      results.length,
    );
    localStorage.setItem("searchResults", JSON.stringify(results));
  }, [results]);

  // Save listFlag to localStorage when it changes
  useEffect(() => {
    console.log(
      "[DEBUG_LOG] listFlag changed, saving to localStorage:",
      listFlag,
    );
    localStorage.setItem("listFlag", JSON.stringify(listFlag));
  }, [listFlag]);

  // Save searchPage so TimeSlipView's "load more on swipe" can update it
  // and SearchView resumes on the same page when revisited.
  useEffect(() => {
    localStorage.setItem("searchPage", JSON.stringify(searchPage));
  }, [searchPage]);

  // Save increRaceIdList to localStorage when it changes
  useEffect(() => {
    console.log(
      "[DEBUG_LOG] increRaceIdList changed, saving to localStorage:",
      increRaceIdList.length,
    );
    localStorage.setItem("increRaceIdList", JSON.stringify(increRaceIdList));
  }, [increRaceIdList]);

  // Toggle comparison modal
  const toggleCompareModal = () => {
    console.log(
      "[DEBUG_LOG] Toggling comparison modal, current state:",
      showCompareModal,
    );
    setShowCompareModal(!showCompareModal);
  };

  // Remove card from selection
  const removeSelectedCard = (cardToRemove) => {
    console.log(
      "[DEBUG_LOG] Removing card from comparison panel:",
      getCardUniqueId(cardToRemove),
    );
    setSelectedCards((prevSelected) =>
      prevSelected.filter(
        (card) => getCardUniqueId(card) !== getCardUniqueId(cardToRemove),
      ),
    );
  };

  // Calculate delta between two values
  const calculateDelta = (value1, value2) => {
    if (!value1 || !value2) return "N/A";
    return (parseFloat(value1) - parseFloat(value2)).toFixed(3);
  };

  // State to store the previous scroll position
  const [lastScrollY, setLastScrollY] = useState(0);
  // State to track if the user is scrolling up
  const [isScrollingUp, setIsScrollingUp] = useState(false);

  // Handler function to detect scroll direction
  const handleScroll = () => {
    // Check current scroll position
    const currentScrollY = window.scrollY;

    // If at the very top, force false
    if (currentScrollY === 0) {
      setIsScrollingUp(false);
      setLastScrollY(0);
      return;
    }

    if (currentScrollY < lastScrollY && currentScrollY > 700) {
      // User is scrolling up
      setIsScrollingUp(true);
    } else if (currentScrollY > lastScrollY) {
      // User is scrolling down
      setIsScrollingUp(false);
    }

    // Update the last scroll position
    setLastScrollY(currentScrollY);
  };

  useEffect(() => {
    // Add the scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY]);

  const [trackList, setTrackList] = useState([
    { trackName: "Loading, please wait..." },
  ]);

  const [trackOptions, setTrackOptions] = useState([]);

  useEffect(() => {
    if (trackList && trackList.length > 0) {
      const uniqueTracks = [
        ...new Set(trackList.map((item) => item.trackName)),
      ];
      setTrackOptions(uniqueTracks);
    }
  }, [trackList]);

  const eventOptions = trackList.filter(
    (item) =>
      item.trackName?.trim().toLowerCase() ===
      trackNamevalue?.trim().toLowerCase(),
  );

  const handleEventNameChange = (e) => {
    const selectedId = e.target.value;

    setSelectedEventValue(selectedId);

    const selectedEvent = eventOptions.find(
      (event) => event.eventId.toString() === selectedId,
    );

    if (selectedEvent) {
      const { eventId, eventName } = selectedEvent;

      setEventName(eventName);

      setEventIdValue(eventId);

      getCategoryList(eventId);

      setCategoryList([]);
      setSelectedCategoryValue("");
      setroundList([]);
      setSelectedRoundValue("");

      // Persist updated selection so other pages (TimeSlipView) also reflect it
      const updatedSelection = {
        trackName: trackNamevalue,
        eventId: String(eventId),
        eventName: eventName,
      };
      localStorage.setItem(
        TRACK_EVENT_SELECTION_KEY,
        JSON.stringify(updatedSelection),
      );
    }
  };

  const getTrackList = async () => {
    try {
      const response = await api.get(TRACK_LIST_API_test, {
        withCredentials: true,
      });

      const data = response.data;
      setTrackList(data);
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  const getCategoryList = async (eventId) => {
    setEventIdValue(eventId);
    try {
      const response = await api.get(API_CATEGORY_LIST_URL, {
        params: { eventId: eventId , userId: userId },
        withCredentials: true,
      });

      const data = response.data;
      setCategoryList(data);
    } catch (error) {
      console.error("Error fetching category data:", error);
    }
  };

  const getSavedSearchList = async (eventId) => {
    try {
      const response = await api.get(API_SAVED_SEARCH_URL, {
        params: { eventId: eventId , userId: userId },
        withCredentials: true,
      });
      const data = response.data;
      setSavedSearchList(data);
    } catch (error) {
      console.error("Error fetching saved search data:", error);
    }
  };

  const handleRoundChange = async (requestCategory) => {
    setCategoryValue(requestCategory);
    try {
      const response = await api.get(API_ROUNDS_LIST_URL, {
        params: {
          category: requestCategory,
          eventId: eventIdValue,
        },
        withCredentials: true,
      });

      const data = response.data;
      setroundList(data);
    } catch (error) {
      console.error("Error fetching round data:", error);
    }
  };

  const handleRoundChangeValue = async (event) => {
    setSelectedRoundValue(event.target.value);
  };

  const handleCategoryChange = async (event) => {
    const selectedCategory = event.target.value;
    setSelectedCategoryValue(selectedCategory);
    setSelectedRoundValue("");
    handleRoundChange(selectedCategory);
  };

  const handleMyFiltersChange = async (event) => {
    if (event.target.value === "0") {
      handleClear(event);
      return;
    }
    const selectedFilter = event.target.value;
    setSelectedFilterName(selectedFilter);
    if (selectedFilter) {
      const selectedSavedSearch = savedSearchList.find(
        (search) => search.name === selectedFilter,
      );

      if (selectedSavedSearch) {
        setSearchName(selectedSavedSearch.name || "");
        setCriteriaId(selectedSavedSearch.id || "");

        const criteria = selectedSavedSearch.criteria;

        setFromDate((criteria.eventFromDate || "").slice(0, 10).trim());
        setToDate((criteria.eventToDate || "").slice(0, 10).trim());
        setSelectedCategoryValue(criteria.category || "");
        setSelectedRoundValue(criteria.round || "");
        setCarClass(criteria.carClass || "");
        setDriverName(criteria.driverName || "");
        setCarNumber(criteria.carNo || "");
        setEtRangeFrom(criteria.startET || "");
        setEtRangeTo(criteria.endET || "");

        setSortBy(criteria.sortBy || "");
        setSortOrder(criteria.sortOrder || "");
      }
    }
  };

  return (
    <div className="search-view-container">
      {/*<div className="event-header-search">*/}
      {/*  <div className="event-details">Race History Search</div>*/}
      {/*</div>*/}

      {!dataLoaded ? (
        // Show loading indicator while data is being loaded
        <div className="loader"></div>
      ) : (
        // Only show search form and other UI elements after data is loaded
        <>
          <div
            className={`search-sticky-header${
              criteriaCollapsed ? " is-collapsed" : ""
            }`}
          >
          <div
            ref={criteriaRef}
            className={"search-container-content"}
          >
            <button
              type="button"
              className="search-criteria-toggle"
              onClick={() => {
                // When expanding while scrolled down, the wrapper un-stickies
                // and snaps back to its natural position near the top of the
                // page. Scroll to top first so the user actually sees the
                // form (and so the auto-collapse listener doesn't immediately
                // fire on the form's now-above-viewport rect).
                if (criteriaCollapsed) {
                  window.scrollTo(0, 0);
                }
                setCriteriaCollapsed((c) => !c);
              }}
              aria-expanded={!criteriaCollapsed}
              aria-controls="search-criteria-form"
            >
              <span>Search criteria</span>
              {criteriaCollapsed ? <FaChevronDown /> : <FaChevronUp />}
            </button>
            <form
              id="search-criteria-form"
              onSubmit={handleSearch}
              hidden={criteriaCollapsed}
            >

               <div className="input-field-dropdown">
                <label htmlFor="SavedSearch">My Filters:</label>
                <select
                  id="SavedSearchSelect"
                  value={selectedFilterName}
                  onChange={handleMyFiltersChange}
                  onClick={() => getSavedSearchList(eventIdValue)}
                >
                  <option value="0">{criteriaId ? "-- Click to Clear --" : "-- Select a Saved Search --"}</option>

                  {savedSearchList.map((search) => (
                    <option key={search.name} value={search.name}>
                      {search.name}
                    </option>
                  ))}
                </select>
              </div>
            
              <div className="input-field">
                <label htmlFor="fromDate">From Date:</label>
                <input
                  type="date"
                  placeholder="dd-mm-yyyy"
                  id="fromDate"
                  value={fromDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFromDate(value);
                    if (toDate && new Date(toDate) < new Date(value)) {
                      setToDate("");
                    }
                  }}
                  required={!!toDate}
                />
                {fromDate && (
                  <button
                    type="button"
                    className="date-clear-btn"
                    onClick={() => setFromDate("")}
                    aria-label="Clear From Date"
                    title="Clear"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="input-field">
                <label htmlFor="toDate">To Date:</label>
                <input
                  type="date"
                  placeholder="dd-mm-yyyy"
                  id="toDate"
                  value={toDate}
                  min={fromDate}
                  onChange={(e) => setToDate(e.target.value)}
                  required={!!fromDate}
                />
                {toDate && (
                  <button
                    type="button"
                    className="date-clear-btn"
                    onClick={() => setToDate("")}
                    aria-label="Clear To Date"
                    title="Clear"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="input-field-dropdown">
                <label htmlFor="date">Track Name:</label>
                <select
                  value={trackNamevalue}
                  disabled={true}
                >
                  <option value={trackNamevalue}>{trackNamevalue}</option>
                </select>
              </div>

              <div className="input-field-dropdown">
                <label htmlFor="date">Event Name:</label>
                <select
                  value={selectedEventValue}
                  disabled={true}
                >
                  <option value={selectedEventValue}>
                    {eventName} ({startDate} to {endDate})
                  </option>
                </select>
              </div>

              <div className="input-field-dropdown">
                <label htmlFor="round">Category:</label>
                <select
                  id="CategorySelect"
                  value={selectedCategoryValue}
                  onChange={handleCategoryChange}
                  onFocus={() => getCategoryList(eventIdValue)}
                >
                  <option value="">-- Select a Category-Name --</option>

                  {/* 👇 fallback for preselected value */}
                  {selectedCategoryValue &&
                    !categoryList.includes(selectedCategoryValue) && (
                      <option value={selectedCategoryValue}>
                        {selectedCategoryValue}
                      </option>
                    )}

                  {categoryList.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                {selectedCategoryValue && (
                  <button type="button" className="date-clear-btn" onClick={() => { setSelectedCategoryValue(""); setSelectedRoundValue("");} }>×</button>
                )}
              </div>

              <div className="input-field-dropdown">
                <label htmlFor="round">Round:</label>
                <select
                  id="RoundSelect"
                  value={selectedRoundValue}
                  onChange={handleRoundChangeValue}
                  onFocus={() => handleRoundChange(selectedCategoryValue)}
                >
                  <option value="">-- Select a Round-Name --</option>

                  {/* 👇 fallback for preselected value */}
                  {selectedRoundValue &&
                    !roundList.includes(selectedRoundValue) && (
                      <option value={selectedRoundValue}>
                        {selectedRoundValue}
                      </option>
                    )}

                  {roundList.map((round) => (
                    <option key={round} value={round}>
                      {round}
                    </option>
                  ))}
                </select>
                {selectedRoundValue && (
                  <button type="button" className="date-clear-btn" onClick={() => setSelectedRoundValue("")}>×</button>
                )}
              </div>
              <div className="input-field">
                <label htmlFor="round">Class:</label>
                <input
                  maxLength={50}
                  type="text"
                  id="round"
                  value={carClass}
                  onChange={(e) => setCarClass(e.target.value)}
                />
                {carClass && (
                  <button type="button" className="date-clear-btn" onClick={() => setCarClass("")}>×</button>
                )}
              </div>

              <div className="input-field">
                <label htmlFor="driverName">Driver Name:</label>
                <input
                  maxLength={50}
                  type="text"
                  id="driverName"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                />
                {driverName && (
                  <button type="button" className="date-clear-btn" onClick={() => setDriverName("")}>×</button>
                )}
              </div>

              <div className="input-field">
                <label htmlFor="carNumber">Car Number:</label>
                <input
                  maxLength={30}
                  type="text"
                  id="carNumber"
                  value={carNumber}
                  onChange={(e) => setCarNumber(e.target.value)}
                />
                {carNumber && (
                  <button type="button" className="date-clear-btn" onClick={() => setCarNumber("")}>×</button>
                )}
              </div>

              <div className="input-field">
                <label htmlFor="carNumber">ET Range:</label>
                <input
                  maxLength={30}
                  type="number"
                  id="carNumber"
                  value={EtRangeFrom}
                  step="0.001"
                  onChange={(e) => setEtRangeFrom(e.target.value)}
                />
                {EtRangeFrom && (
                  <button type="button" className="date-clear-btn" onClick={() => setEtRangeFrom("")}>×</button>
                )}
                -
                <input
                  maxLength={30}
                  type="number"
                  id="carNumber"
                  value={EtRangeTo}
                  step="0.001"
                  onChange={(e) => setEtRangeTo(e.target.value)}
                />
                {EtRangeTo && (
                  <button type="button" className="date-clear-btn" onClick={() => setEtRangeTo("")}>×</button>
                )}
              </div>

               <div className="input-field-dropdown">
                <label htmlFor="round">Sort By:</label>
                <select
                  id="RoundSelect"
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setShowSortHint(false); }}
                >
                  <option value="">-- Select Sort By Value --</option>
                  <option value="et">ET</option>
                  <option value="overUnder">OVER/UNDER</option>
                  <option value="rt">REACTION TIME</option>
                  <option value="raceDate">TIME</option>
                </select>
                {sortBy && (
                  <button type="button" className="date-clear-btn" onClick={() => { setSortBy(""); setSortOrder(""); }}>×</button>
                )}
              </div>

               <div className="input-field-dropdown">
                <label htmlFor="sortOrder">Sort Order:</label>
                {sortBy ? (
                  <select
                    id="sortOrder"
                    value={sortOrder}
                    onChange={(e) => { setSortOrder(e.target.value); setShowSortHint(false); }}
                  >
                    <option value="">-- Select Sort Order Value --</option>
                    <option value="ASC">ASC</option>
                    <option value="DESC">DESC</option>
                  </select>
                ) : (
                  <select
                    id="sortOrder"
                    value=""
                    onChange={() => setShowSortHint(true)}
                    onMouseEnter={() => setShowSortHint(true)}
                    onMouseLeave={() => setShowSortHint(false)}
                  >
                    <option value="">-- Select Sort Order Value --</option>
                    <option value="">*****Please Select Sort By first*****</option>
                  </select>
                )}
                {sortOrder && (
                  <button type="button" className="date-clear-btn" onClick={() => setSortOrder("")}>×</button>
                )}
              </div>
              {showSortHint && sortBy && !sortOrder && (
                <div className="sort-order-hint">Please select Sort Order</div>
              )}

              <div className="input-field">
                <label htmlFor="searchName">Save Filter:</label>
                <input
                  maxLength={50}
                  type="text"
                  id="searchName"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
                {searchName && (
                  <button type="button" className="date-clear-btn" onClick={() => setSearchName("")}>×</button>
                )}
              </div>
              <div className="button-group-searchView">
                {searchName && (
                <button type="button" onClick={handleSaveSearch} className="search-button">
                  Save Filter
                </button>
                )}
                {(criteriaId ) && (
                 <button
                  type="button"
                  onClick={(e) => handleDeleteReportType(criteriaId, e)}
                  className="search-button"
                >
                  Delete report type
                </button>
                  )}

                <button type="submit" className="search-button">
                  <FaSearch /> Search
                </button>

                <button
                  type="button"
                  onClick={handleClear}
                  className="search-button"
                >
                  Clear
                </button>
              </div>
            </form>
          </div>

          {/* Comparison Panel - only shown when cards are selected */}
          {selectedCards.length > 0 && (
            <div className="comparison-panel">
              <div className="comparison-cards-container">
                {selectedCards.map((card, index) => (
                  <div key={index} className="comparison-card">
                    <div className="comparison-card-info">
                      <div className="comparison-card-car">
                        Car #{card.carNumber}
                      </div>
                      <div className="comparison-card-time">
                        {formatDateRaceUniqueId(card.raceUniqueId)}
                      </div>
                    </div>
                    <button
                      className="comparison-card-remove"
                      onClick={() => removeSelectedCard(card)}
                      title="Remove from comparison"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={toggleCompareModal}
                  className="search-button compare-button"
                  disabled={selectedCards.length < 2 || selectedCards.length > 3}
                >
                  <FaExchangeAlt />
                </button>
              </div>
            </div>
          )}
          </div>
        </>
      )}

      {dataLoaded && (
        <>
          {loading ? (
            <div className="loader"></div>
          ) : results.length > 0 ? (
            <>
              <div className="results-container">
                {results.map((result, index) => (
                  <div key={index} className="result-card">
                    <div className="result-card-columns">
                      <div className="result-left">
                        <div>
                          <strong>{result.driver}</strong>
                        </div>
                        <div>
                          <strong>Car #:</strong> {result.carNumber}
                        </div>
                        <div>
                          <strong>Class:</strong> {result.carClass}
                        </div>
                        <div>
                          <strong>Opponent Name:</strong>{" "}
                          {result.opponentDriverName}
                        </div>
                        <div>
                          <strong>Opponent Car #:</strong>{" "}
                          {result.opponentDriverCarNo}
                        </div>
                      </div>

                      <div className="result-right">
                        <div style={{ color: "#007bff" }}>
                          <strong>
                            {formatDateRaceUniqueId(result.raceUniqueId)}
                          </strong>
                        </div>
                        <div>
                          <strong>Round:</strong> {result.round}
                        </div>
                        <div>
                          {result.win ? (
                            <span className="win-text">WIN</span>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    <div className="result-card-header">
                      <input
                        type="checkbox"
                        className="compare-checkbox"
                        checked={selectedCards.some(
                          (card) =>
                            getCardUniqueId(card) === getCardUniqueId(result),
                        )}
                        onChange={() => handleCardSelection(result)}
                      />
                      <label className="compare-label">Compare</label>

                      <button
                        className="timeslip-btn"
                        onClick={() =>
                          handleClick(
                            result.date,
                            result.raceDayRaceId,
                            result.increRaceId,
                            result.raceKey,
                            increRaceIdList,
                            listFlag,
                          )
                        }
                      >
                        Time Slip
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={loadMoreRef} className="load-more-sentinel">
                {hasMore && loadingMore && (
                  <div className="load-more-spinner">Loading more…</div>
                )}
                {!hasMore && results.length > 0 && (
                  <div className="load-more-end">No more results</div>
                )}
              </div>
            </>
          ) : (
            // 👇 This shows when NO results
            <div
              style={{
                textAlign: "center",
                marginTop: "20px",
                color: "#faf8f8",
              }}
            >
              No data available at the moment.
            </div>
          )}
        </>
      )}

      {/* Comparison Modal - only shown when dataLoaded is true and there are 2 or 3 selected cards */}
      {dataLoaded && showCompareModal && (selectedCards.length === 2 || selectedCards.length === 3) && (
        <div className="modal-overlay" onClick={toggleCompareModal}>
          <div className={`modal-content compare-modal ${selectedCards.length === 3 ? 'compare-modal-3' : ''}`} onClick={(e) => e.stopPropagation()}>
            <h3 className="compare-title">Compare Runs</h3>
            <table className={`compare-table ${selectedCards.length === 3 ? 'compare-table-3' : ''}`}>
              <thead>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>Metrics</th>
                  <th>{selectedCards[0].driver}</th>
                  <th>{selectedCards[1].driver}</th>
                  {selectedCards.length === 3 && <th>{selectedCards[2].driver}</th>}
                  {selectedCards.length === 2 && <th>Delta</th>}
                  {selectedCards.length === 3 && (
                    <>
                      <th>Delta 1-2</th>
                      <th>Delta 2-3</th>
                      <th>Delta 1-3</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>Date</th>
                  <td>
                    {formatDateRaceUniqueId(selectedCards[0].raceUniqueId)}
                  </td>
                  <td>
                    {formatDateRaceUniqueId(selectedCards[1].raceUniqueId)}
                  </td>
                  {selectedCards.length === 3 && (
                    <td>
                      {formatDateRaceUniqueId(selectedCards[2].raceUniqueId)}
                    </td>
                  )}
                  {selectedCards.length === 2 && <td>-</td>}
                  {selectedCards.length === 3 && (
                    <>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>Car Number</th>
                  <td>{selectedCards[0].carNumber}</td>
                  <td>{selectedCards[1].carNumber}</td>
                  {selectedCards.length === 3 && (
                    <>
                      <td>{selectedCards[2].carNumber}</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                    </>
                  )}
                  {selectedCards.length === 2 && <td>-</td>}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>Class</th>
                  <td>{selectedCards[0].carClass}</td>
                  <td>{selectedCards[1].carClass}</td>
                  {selectedCards.length === 3 && (
                    <>
                      <td>{selectedCards[2].carClass}</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                    </>
                  )}
                  {selectedCards.length === 2 && <td>-</td>}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>Round</th>
                  <td>{selectedCards[0].round}</td>
                  <td>{selectedCards[1].round}</td>
                  {selectedCards.length === 3 && (
                    <>
                      <td>{selectedCards[2].round}</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                    </>
                  )}
                  {selectedCards.length === 2 && <td>-</td>}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>Result</th>
                  <td>{selectedCards[0].win ? "WIN" : "LOSS"}</td>
                  <td>{selectedCards[1].win ? "WIN" : "LOSS"}</td>
                  {selectedCards.length === 3 && (
                    <>
                      <td>{selectedCards[2].win ? "WIN" : "LOSS"}</td>
                      <td>-</td>
                      <td>-</td>
                      <td>-</td>
                    </>
                  )}
                  {selectedCards.length === 2 && <td>-</td>}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>Dial-in Time</th>
                  <td>{selectedCards[0].dial_in}</td>
                  <td>{selectedCards[1].dial_in}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].dial_in}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(
                        selectedCards[0].dial_in,
                        selectedCards[1].dial_in,
                      )}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(
                          selectedCards[0].dial_in,
                          selectedCards[1].dial_in,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[1].dial_in,
                          selectedCards[2].dial_in,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[0].dial_in,
                          selectedCards[2].dial_in,
                        )}
                      </td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>Reaction Time</th>
                  <td>{selectedCards[0].rt}</td>
                  <td>{selectedCards[1].rt}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].rt}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(selectedCards[0].rt, selectedCards[1].rt)}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(selectedCards[0].rt, selectedCards[1].rt)}
                      </td>
                      <td>
                        {calculateDelta(selectedCards[1].rt, selectedCards[2].rt)}
                      </td>
                      <td>
                        {calculateDelta(selectedCards[0].rt, selectedCards[2].rt)}
                      </td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>60' Time</th>
                  <td>{selectedCards[0].ft60}</td>
                  <td>{selectedCards[1].ft60}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].ft60}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(
                        selectedCards[0].ft60,
                        selectedCards[1].ft60,
                      )}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft60,
                          selectedCards[1].ft60,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[1].ft60,
                          selectedCards[2].ft60,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft60,
                          selectedCards[2].ft60,
                        )}
                      </td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>330' Time</th>
                  <td>{selectedCards[0].ft330}</td>
                  <td>{selectedCards[1].ft330}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].ft330}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(
                        selectedCards[0].ft330,
                        selectedCards[1].ft330,
                      )}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft330,
                          selectedCards[1].ft330,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[1].ft330,
                          selectedCards[2].ft330,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft330,
                          selectedCards[2].ft330,
                        )}
                      </td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>1/8 Mile ET</th>
                  <td>{selectedCards[0].ft8}</td>
                  <td>{selectedCards[1].ft8}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].ft8}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(selectedCards[0].ft8, selectedCards[1].ft8)}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(selectedCards[0].ft8, selectedCards[1].ft8)}
                      </td>
                      <td>
                        {calculateDelta(selectedCards[1].ft8, selectedCards[2].ft8)}
                      </td>
                      <td>
                        {calculateDelta(selectedCards[0].ft8, selectedCards[2].ft8)}
                      </td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>1/8 Mile MPH</th>
                  <td>{selectedCards[0].ft8mph}</td>
                  <td>{selectedCards[1].ft8mph}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].ft8mph}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(
                        selectedCards[0].ft8mph,
                        selectedCards[1].ft8mph,
                      )}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft8mph,
                          selectedCards[1].ft8mph,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[1].ft8mph,
                          selectedCards[2].ft8mph,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft8mph,
                          selectedCards[2].ft8mph,
                        )}
                      </td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>1000' Time</th>
                  <td>{selectedCards[0].ft1000}</td>
                  <td>{selectedCards[1].ft1000}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].ft1000}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(
                        selectedCards[0].ft1000,
                        selectedCards[1].ft1000,
                      )}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft1000,
                          selectedCards[1].ft1000,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[1].ft1000,
                          selectedCards[2].ft1000,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft1000,
                          selectedCards[2].ft1000,
                        )}
                      </td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>1/4 Mile ET</th>
                  <td>{selectedCards[0].ft4}</td>
                  <td>{selectedCards[1].ft4}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].ft4}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(selectedCards[0].ft4, selectedCards[1].ft4)}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(selectedCards[0].ft4, selectedCards[1].ft4)}
                      </td>
                      <td>
                        {calculateDelta(selectedCards[1].ft4, selectedCards[2].ft4)}
                      </td>
                      <td>
                        {calculateDelta(selectedCards[0].ft4, selectedCards[2].ft4)}
                      </td>
                    </>
                  )}
                </tr>
                <tr>
                  <th className={selectedCards.length === 3 ? 'metrics-col-sticky' : ''}>1/4 Mile MPH</th>
                  <td>{selectedCards[0].ft4mph}</td>
                  <td>{selectedCards[1].ft4mph}</td>
                  {selectedCards.length === 3 && <td>{selectedCards[2].ft4mph}</td>}
                  {selectedCards.length === 2 && (
                    <td>
                      {calculateDelta(
                        selectedCards[0].ft4mph,
                        selectedCards[1].ft4mph,
                      )}
                    </td>
                  )}
                  {selectedCards.length === 3 && (
                    <>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft4mph,
                          selectedCards[1].ft4mph,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[1].ft4mph,
                          selectedCards[2].ft4mph,
                        )}
                      </td>
                      <td>
                        {calculateDelta(
                          selectedCards[0].ft4mph,
                          selectedCards[2].ft4mph,
                        )}
                      </td>
                    </>
                  )}
                </tr>
              </tbody>
            </table>

            <button className="compare-close-btn" onClick={toggleCompareModal}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchView;
